import { createContext, useContext, useReducer, useCallback, useRef, ReactNode } from "react";

export interface TabItem {
  path: string;
  label: string;
}

interface TabContextType {
  tabs: TabItem[];
  activeTab: string;
  addTab: (path: string, label: string) => void;
  closeTab: (path: string) => void;
  closeAllTabs: () => void;
  closeOtherTabs: (path: string) => void;
  setActiveTab: (path: string) => void;
  updateTabLabel: (path: string, label: string) => void;
}

const TAB_STORAGE_KEYS: Record<string, string[]> = {
  "/pages/opportunities": ["opportunities_list"],
  "/pages/leads": ["leads_list"],
  "/pages/customers": ["customers_list"],
  "/pages/vehicles": ["vehicles_list"],
  "/pages/opportunities/plans": ["plan_list"],
};

const TabContext = createContext<TabContextType | null>(null);

function clearFilterState(path: string) {
  const keys = TAB_STORAGE_KEYS[path];
  if (keys) {
    keys.forEach((key) => sessionStorage.removeItem(`page_filter_${key}`));
  }
}

type TabState = {
  tabs: TabItem[];
  activeTab: string;
};

type TabAction =
  | { type: "ADD_TAB"; path: string; label: string }
  | { type: "CLOSE_TAB"; path: string }
  | { type: "CLOSE_ALL" }
  | { type: "CLOSE_OTHER"; path: string }
  | { type: "SET_ACTIVE"; path: string }
  | { type: "UPDATE_LABEL"; path: string; label: string };

function tabReducer(state: TabState, action: TabAction): TabState {
  switch (action.type) {
    case "ADD_TAB": {
      if (state.tabs.some(t => t.path === action.path)) {
        return { ...state, activeTab: action.path };
      }
      return {
        tabs: [...state.tabs, { path: action.path, label: action.label }],
        activeTab: action.path,
      };
    }
    case "CLOSE_TAB": {
      clearFilterState(action.path);
      const newTabs = state.tabs.filter(t => t.path !== action.path);
      if (action.path === state.activeTab) {
        if (newTabs.length > 0) {
          const idx = state.tabs.findIndex(t => t.path === action.path);
          const newIdx = Math.min(idx, newTabs.length - 1);
          return { tabs: newTabs, activeTab: newTabs[newIdx].path };
        }
        return { tabs: [], activeTab: "" };
      }
      return { ...state, tabs: newTabs };
    }
    case "CLOSE_ALL":
      state.tabs.forEach(t => clearFilterState(t.path));
      return { tabs: [], activeTab: "" };
    case "CLOSE_OTHER": {
      const toRemove = state.tabs.filter(t => t.path !== action.path);
      toRemove.forEach(t => clearFilterState(t.path));
      return {
        tabs: state.tabs.filter(t => t.path === action.path),
        activeTab: action.path,
      };
    }
    case "SET_ACTIVE":
      return { ...state, activeTab: action.path };
    case "UPDATE_LABEL":
      return {
        ...state,
        tabs: state.tabs.map(t =>
          t.path === action.path ? { ...t, label: action.label } : t
        ),
      };
    default:
      return state;
  }
}

const initialState: TabState = { tabs: [], activeTab: "" };

export function TabProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(tabReducer, initialState);

  const addTab = useCallback((path: string, label: string) => {
    dispatch({ type: "ADD_TAB", path, label });
  }, []);

  const closeTab = useCallback((path: string) => {
    dispatch({ type: "CLOSE_TAB", path });
  }, []);

  const closeAllTabs = useCallback(() => {
    dispatch({ type: "CLOSE_ALL" });
  }, []);

  const closeOtherTabs = useCallback((path: string) => {
    dispatch({ type: "CLOSE_OTHER", path });
  }, []);

  const setActiveTab = useCallback((path: string) => {
    dispatch({ type: "SET_ACTIVE", path });
  }, []);

  const updateTabLabel = useCallback((path: string, label: string) => {
    dispatch({ type: "UPDATE_LABEL", path, label });
  }, []);

  return (
    <TabContext.Provider value={{ tabs: state.tabs, activeTab: state.activeTab, addTab, closeTab, closeAllTabs, closeOtherTabs, setActiveTab, updateTabLabel }}>
      {children}
    </TabContext.Provider>
  );
}

export function useTabs() {
  const ctx = useContext(TabContext);
  if (!ctx) throw new Error("useTabs must be used within TabProvider");
  return ctx;
}
